import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { getProduct, products } from "@/data/products";
import { getCluster } from "@/data/clusters";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

const SectionBlock = ({ section }) => (
  <Reveal as="section" style={{ marginTop: "2.6rem" }}>
    <h2
      className="display"
      style={{
        fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
        margin: "0 0 1.1rem",
        letterSpacing: "-.01em",
        lineHeight: 1.15,
      }}
    >
      {section.h2}
    </h2>
    {(section.body || []).map((para, i) => (
      <p
        key={i}
        style={{
          color: "var(--ink-soft)",
          fontSize: "1.04rem",
          lineHeight: 1.75,
          margin: "0 0 1.1rem",
          maxWidth: "68ch",
        }}
      >
        {para}
      </p>
    ))}
    {section.bullets && (
      <ul className="cap-list" style={{ marginTop: ".6rem", maxWidth: "70ch" }}>
        {section.bullets.map((b, i) => (
          <li key={i}>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
  </Reveal>
);

export const ProductDetail = () => {
  const { id } = useParams();
  const data = getProduct(id);

  const relatedClusters = data
    ? data.clusters.map((cid) => getCluster(cid)).filter(Boolean)
    : [];

  useSeo({
    title: data ? data.title : "Product",
    description: data ? data.metaDescription : undefined,
    path: data ? `/products/${data.id}` : "/products",
    image: data?.image,
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              "@id": `${SITE_URL}/products/${data.id}#product`,
              name: data.name,
              description: data.metaDescription,
              image: data.image,
              category: data.parentLabel,
              brand: { "@id": `${SITE_URL}#organization` },
              offers: {
                "@type": "AggregateOffer",
                availability: "https://schema.org/InStock",
                priceCurrency: "USD",
                offerCount: "20+",
                seller: { "@id": `${SITE_URL}#organization` },
              },
            },
            {
              "@type": "FAQPage",
              mainEntity: (data.faqs || []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: f.a,
                },
              })),
            },
            breadcrumb([
              { name: "Home", item: `${SITE_URL}/` },
              { name: "Products", item: `${SITE_URL}/products` },
              { name: data.name, item: `${SITE_URL}/products/${data.id}` },
            ]),
            {
              "@type": ["Organization", "LocalBusiness"],
              "@id": `${SITE_URL}#organization`,
              name: "Asia Sourcing",
              url: SITE_URL,
              logo: `${SITE_URL}/assets/og.jpg`,
              address: {
                "@type": "PostalAddress",
                addressLocality: "Moradabad",
                addressRegion: "Uttar Pradesh",
                addressCountry: "IN",
              },
            },
          ],
        }
      : undefined,
  });

  if (!data) return <Navigate to="/products" replace />;

  const relatedProducts = products
    .filter((p) => p.id !== data.id && p.parentCategory === data.parentCategory)
    .slice(0, 6);

  return (
    <div data-testid="product-detail-page">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}
      >
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p className="mono" style={{ fontSize: ".72rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase" }}>
            <Link to="/" style={{ color: "inherit" }}>Home</Link> · <Link to="/products" style={{ color: "inherit" }}>Products</Link> · {data.name}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="product-eyebrow">{data.eyebrow}</p>
        </Reveal>
        <WordReveal
          as="h1"
          text={data.h1}
          className="display"
          stagger={70}
          testId="product-h1"
        />
        <Reveal as="p" delay={200} style={{ maxWidth: "72ch", color: "var(--ink-soft)", fontSize: "1.1rem", marginTop: "1.4rem", lineHeight: 1.65 }}>
          {data.intro}
        </Reveal>
      </header>

      {/* FACTS strip */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
        data-testid="product-facts"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {data.facts.map(([k, v]) => (
            <Reveal key={k}>
              <span className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase" }}>{k}</span>
              <p style={{ fontFamily: "var(--display)", fontWeight: 380, fontSize: "1.2rem", margin: ".3rem 0 0", letterSpacing: "-.005em" }}>{v}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY sections */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {(data.sections || []).map((s, i) => (
          <SectionBlock key={i} section={s} />
        ))}
      </section>

      {/* FAQ */}
      {data.faqs && data.faqs.length > 0 && (
        <section
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)", maxWidth: "820px" }}
          data-testid="product-faq"
        >
          <Reveal as="p" className="eyebrow">Frequently asked</Reveal>
          <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
            {data.name} — buyer questions
          </Reveal>
          {data.faqs.map((f, i) => (
            <Reveal key={i} as="details" style={{ borderBottom: "1px solid var(--line)", padding: "1.4rem 0" }}>
              <summary style={{ cursor: "pointer", fontFamily: "var(--display)", fontSize: "1.1rem", color: "var(--ink)", listStyle: "none" }}>
                {f.q}
              </summary>
              <p style={{ marginTop: ".8rem", color: "var(--ink-soft)", lineHeight: 1.7, maxWidth: "68ch" }}>{f.a}</p>
            </Reveal>
          ))}
        </section>
      )}

      {/* RELATED clusters */}
      {relatedClusters.length > 0 && (
        <section
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
          data-testid="product-clusters"
        >
          <Reveal as="p" className="eyebrow">Manufacturing clusters</Reveal>
          <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
            Where we source {data.name.toLowerCase()}
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.2rem" }}>
            {relatedClusters.map((c) => (
              <Reveal key={c.id}>
                <Link to={`/clusters/${c.id}`} style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }}>
                  <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                    {c.state} · {c.nickname}
                  </p>
                  <p style={{ fontFamily: "var(--display)", fontSize: "1.2rem", margin: ".4rem 0 0" }}>{c.name}</p>
                  <p style={{ color: "var(--ink-soft)", fontSize: ".9rem", margin: ".4rem 0 0" }}>{c.material}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* RELATED products */}
      {relatedProducts.length > 0 && (
        <section
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
          data-testid="product-related"
        >
          <Reveal as="p" className="eyebrow">Related products</Reveal>
          <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
            More in {data.parentLabel}
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.2rem" }}>
            {relatedProducts.map((p) => (
              <Reveal key={p.id}>
                <Link to={`/products/${p.id}`} style={{ display: "block", padding: "1.2rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }}>
                  <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".68rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                    Product
                  </p>
                  <p style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".4rem 0 0" }}>{p.name}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">
          Source {data.name.toLowerCase()} from India.
        </Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link
            to={`/contact?category=${encodeURIComponent(data.name)}`}
            className="btn btn-fill btn-lg btn-glow"
            data-testid="product-cta-enquiry"
          >
            Start a sourcing brief
          </Link>
          <Link to={`/categories/${data.parentCategory}`} className="btn btn-lg" data-testid="product-cta-parent">
            Explore {data.parentLabel}
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default ProductDetail;
